import React, { useState, useEffect, useMemo, useRef } from 'react';
import { GiFairyWings } from 'react-icons/gi';
import { FaDonate, FaUser, FaPhoneAlt, FaIdCard, FaMapMarkerAlt, FaMoneyBillWave, FaVenusMars, FaCalendarAlt, FaCity, FaMap } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { Helmet } from "react-helmet-async";
import axiosInstance from '../../api/axiosInstance';
import './DonateNow.css';
import statesDistrictsData from '../../JsonData/india-states-districts-latest.json';

const DONATION_TYPES = ['General Donation (सामान्य दान)', 'Shiksha Nek (शिक्षा दान)'];

const SearchableSelect = ({ name, value, options, onChange, placeholder, icon: Icon, required, readOnly, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const wrapperRef = useRef(null);

  useEffect(() => { setSearchTerm(value); }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
        if (searchTerm && !options.includes(searchTerm)) {
          setSearchTerm('');
          onChange({ target: { name, value: '' } });
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [searchTerm, options, name, onChange]);

  const filtered = options.filter(o => o.toLowerCase().includes(searchTerm.toLowerCase()));

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
    onChange({ target: { name, value: e.target.value } });
  };

  const handleKeyDown = (e) => {
    if (!isOpen) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered.length > 0) {
        const selected = filtered[highlightedIndex];
        setSearchTerm(selected);
        onChange({ target: { name, value: selected } });
        setIsOpen(false);
      }
    }
  };

  useEffect(() => { setHighlightedIndex(0); }, [searchTerm]);

  return (
    <div className="searchable-wrap" ref={wrapperRef}>
      {Icon && <Icon className="donate-now-input-icon" />}
      <input 
        type="text" name={name} value={searchTerm} onChange={handleInputChange} onKeyDown={handleKeyDown}
        onFocus={() => { if (!readOnly && !disabled) setIsOpen(true); }} 
        placeholder={placeholder} className="donate-now-input" 
        required={required} readOnly={readOnly} disabled={disabled} autoComplete="off" 
        style={{ backgroundColor: disabled || readOnly ? '#f1f5f9' : '#fff', cursor: disabled || readOnly ? 'not-allowed' : 'text' }}
      />
      {isOpen && !readOnly && !disabled && (
        <ul className="searchable-dropdown-list">
          {filtered.length > 0 ? filtered.map((opt, idx) => (
            <li 
              key={opt} className={idx === highlightedIndex ? 'highlighted' : ''}
              onMouseEnter={() => setHighlightedIndex(idx)}
              onMouseDown={(e) => {
                e.preventDefault();
                setSearchTerm(opt); onChange({ target: { name, value: opt } }); setIsOpen(false); 
              }}
            >
              {opt}
            </li>
          )) : <li className="searchable-no-data">No matching option</li>}
        </ul>
      )}
    </div>
  );
};

const DonateNow = () => {
  const [formData, setFormData] = useState({
    mobileNo: '', name: '', fatherName: '', gender: '', birthDate: '', aadhaarNo: '', panNo: '', currentAddress: '', permanentAddress: '', isSameAddress: false, state: '', district: '', donationType: '', amount: ''
  });

  const [isExisting, setIsExisting] = useState(false);
  const [fetchedData, setFetchedData] = useState({});

  const allStates = useMemo(() => statesDistrictsData.map((item) => item.state).filter(Boolean).sort(), []);
  const availableDistricts = useMemo(() => {
    if (!formData.state) return [];
    const found = statesDistrictsData.find((item) => item.state === formData.state);
    return found?.districts?.sort() || [];
  }, [formData.state]);

  const maskAadhaar = (val) => val && val.length > 4 ? 'X'.repeat(val.length - 4) + val.slice(-4) : val;
  const maskPan = (val) => val && val.length > 1 ? val.charAt(0) + 'X'.repeat(val.length - 1) : val;

  const handleMobileChange = (e) => {
    let val = e.target.value.replace(/\D/g, ''); 
    if (val.length > 0 && !/^[6-9]/.test(val)) val = val.substring(1); 

    if (val.length < 10 && isExisting) {
      setIsExisting(false);
      setFetchedData({});
      setFormData({
        mobileNo: val, name: '', fatherName: '', gender: '', birthDate: '', aadhaarNo: '', panNo: '', currentAddress: '', permanentAddress: '', isSameAddress: false, state: '', district: '', donationType: formData.donationType, amount: formData.amount
      });
    } else {
      setFormData(prev => ({ ...prev, mobileNo: val }));
    }
  };

  useEffect(() => {
    if (formData.mobileNo.length !== 10 || isExisting) return;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    axiosInstance.get(`/api/donors/public/phone/${formData.mobileNo}`)
      .then(res => {
        const data = res.data;
        if (data.success && data.data) {
          const d = data.data;
          setIsExisting(true);
          setFetchedData(d);
          setFormData(prev => ({
            ...prev,
            name: d.name || prev.name,
            fatherName: d.fatherName || prev.fatherName,
            gender: d.gender || prev.gender,
            birthDate: d.birthDate ? d.birthDate.split('T')[0] : prev.birthDate,
            aadhaarNo: d.aadharNo ? maskAadhaar(d.aadharNo) : prev.aadhaarNo,
            panNo: d.panNo ? maskPan(d.panNo) : prev.panNo,
            currentAddress: d.currentAddress || prev.currentAddress,
            permanentAddress: d.permanentAddress || prev.permanentAddress,
            isSameAddress: !!d.isSameAddress,
            state: d.state || prev.state,
            district: d.district || prev.district
          }));
        }
      })
      .catch(() => {})
      .finally(() => clearTimeout(timer));
    return () => { clearTimeout(timer); controller.abort(); };
  }, [formData.mobileNo, isExisting]);

  const handleChange = (e) => {
    let { name, value } = e.target;
    if (name === 'name') value = value.replace(/[^a-zA-Z\s]/g, ''); 
    if (name === 'fatherName') value = value.replace(/[^a-zA-Z\s]/g, ''); 
    if (name === 'amount') value = value.replace(/\D/g, ''); 
    if (name === 'aadhaarNo') value = value.replace(/\D/g, ''); 
    if (name === 'panNo') value = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase(); 

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCurrentAddressChange = (e) => {
    const val = e.target.value;
    setFormData(prev => ({
      ...prev,
      currentAddress: val,
      permanentAddress: prev.isSameAddress ? val : prev.permanentAddress
    }));
  };

  const handleSameAddressCheckboxChange = (e) => {
    const checked = e.target.checked;
    setFormData(prev => ({
      ...prev,
      isSameAddress: checked,
      permanentAddress: checked ? prev.currentAddress : ''
    }));
  };

  const handleStateChange = (e) => {
    setFormData(prev => ({ ...prev, state: e.target.value, district: '' })); 
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.mobileNo.length !== 10) return Swal.fire('Error', 'Mobile number must be 10 digits.', 'error');
    if (!formData.fatherName) return Swal.fire('Error', "Father's Name is required.", 'error');
    if (!formData.amount || Number(formData.amount) <= 0) return Swal.fire('Error', 'Please enter a valid donation amount.', 'error');
    if (!formData.currentAddress) return Swal.fire('Error', 'Current Address is required.', 'error');
    if (!formData.permanentAddress) return Swal.fire('Error', 'Permanent Address is required.', 'error');
    if (formData.panNo && formData.panNo.length !== 10 && !isExisting) return Swal.fire('Error', 'PAN Number must be exactly 10 characters.', 'error');

    const payload = {
      ...formData,
      aadharNo: (isExisting && fetchedData.aadharNo) ? fetchedData.aadharNo : formData.aadhaarNo,
      panNo: (isExisting && fetchedData.panNo) ? fetchedData.panNo : formData.panNo
    };

    try {
      Swal.fire({ title: 'Processing...', text: 'Connecting to Payment Gateway', allowOutsideClick: false, didOpen: () => { Swal.showLoading(); } });
      const res = await axiosInstance.post('/api/payment/initiate', payload);
      const resData = res.data;
      if (resData && resData.success && resData.redirectURI) {
        window.location.href = resData.redirectURI;
      } else {
        Swal.fire('Error', resData?.message || 'Payment initiation failed', 'error');
      }
    } catch (error) {
      const msg = error?.response?.data?.message || error.message || 'Server error. Please try again.';
      Swal.fire('Error', msg, 'error');
    }
  };

  return (
    <>
      <Helmet><title>Donate Now | Rajput Shiksha Kosh</title>
        <meta
          name="description"
          content="Your contribution helps us create better educational opportunities. Donate now to support Rajput Shiksha Kosh."
        />
      </Helmet>
    <div className="donate-now-wrapper">
      <div className="donate-now-container">
        <div className="donate-now-header">
          <div className="donate-now-header-icon-wrap"><FaDonate className="donate-now-header-icon" /></div>
          <h2 className="donate-now-title">DONATE NOW</h2>
          <p className="donate-now-subtitle">Your contribution helps us create better educational opportunities.</p>
          <div className="donate-now-divider"><span className="donate-now-divider-line"></span><GiFairyWings className="donate-now-divider-icon" /><span className="donate-now-divider-line"></span></div>
        </div>

        <div className="donate-now-form-card">
          <form onSubmit={handleSubmit}>
            <div className="donate-now-grid">
              
              <div className="donate-now-field donate-now-full-width">
                <label className="donate-now-label">Enter Contact Number <span style={{ color: 'red' }}>*</span></label>
                <div className="donate-now-input-wrap">
                  <FaPhoneAlt className="donate-now-input-icon" />
                  <span className="donate-now-phone-prefix">+91</span>
                  <input type="tel" name="mobileNo" value={formData.mobileNo} onChange={handleMobileChange} maxLength="10" placeholder="Enter 10-digit mobile number" className="donate-now-input donate-now-phone-input" required />
                  {isExisting && <span className="donate-now-verified-badge">Verified</span>}
                </div>
              </div>

              <div className="donate-now-field">
                <label className="donate-now-label">Full Name <span style={{ color: 'red' }}>*</span></label>
                <div className="donate-now-input-wrap">
                  <FaUser className="donate-now-input-icon" />
                  <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Enter full name" className="donate-now-input" required readOnly={isExisting && !!fetchedData.name} />
                </div>
              </div>

              <div className="donate-now-field">
                <label className="donate-now-label">Father's Name <span style={{ color: 'red' }}>*</span></label>
                <div className="donate-now-input-wrap">
                  <FaUser className="donate-now-input-icon" />
                  <input type="text" name="fatherName" value={formData.fatherName} onChange={handleChange} placeholder="Enter father's name" className="donate-now-input" required readOnly={isExisting && !!fetchedData.fatherName} />
                </div>
              </div>

              <div className="donate-now-field">
                <label className="donate-now-label">Donation Type <span style={{ color: 'red' }}>*</span></label>
                <select name="donationType" value={formData.donationType} onChange={handleChange} className="donate-now-select" required>
                  <option value="">Select Donation Type</option>
                  {DONATION_TYPES.map(item => <option key={item} value={item}>{item}</option>)}
                </select>
              </div>

              <div className="donate-now-field">
                <label className="donate-now-label">Donation Amount <span style={{ color: 'red' }}>*</span></label>
                <div className="donate-now-input-wrap">
                  <FaMoneyBillWave className="donate-now-input-icon" />
                  <input type="text" name="amount" value={formData.amount} onChange={handleChange} placeholder="Enter Amount" className="donate-now-input" required />
                </div>
              </div>

              <div className="donate-now-field">
                <label className="donate-now-label">Gender (Optional)</label>
                <div className="donate-now-input-wrap">
                  <FaVenusMars className="donate-now-input-icon" />
                  <select name="gender" value={formData.gender} onChange={handleChange} className="donate-now-select has-icon" disabled={isExisting && !!fetchedData.gender}>
                    <option value="">Select Gender</option><option value="Male">Male</option><option value="Female">Female</option><option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="donate-now-field">
                <label className="donate-now-label">Birth Date (Optional)</label>
                <div className="donate-now-input-wrap">
                  <FaCalendarAlt className="donate-now-input-icon" />
                  <input type="date" name="birthDate" value={formData.birthDate} onChange={handleChange} className="donate-now-input" readOnly={isExisting && !!fetchedData.birthDate} />
                </div>
              </div>

              <div className="donate-now-field">
                <label className="donate-now-label">Aadhaar Card Number (Optional)</label>
                <div className="donate-now-input-wrap">
                  <FaIdCard className="donate-now-input-icon" />
                  <input type="text" name="aadhaarNo" value={formData.aadhaarNo} onChange={handleChange} maxLength="12" placeholder="Enter Aadhaar number" className="donate-now-input" readOnly={isExisting && !!fetchedData.aadharNo} />
                </div>
              </div>

              <div className="donate-now-field">
                <label className="donate-now-label">PAN Card Number (Optional)</label>
                <div className="donate-now-input-wrap">
                  <FaIdCard className="donate-now-input-icon" />
                  <input type="text" name="panNo" value={formData.panNo} onChange={handleChange} maxLength="10" placeholder="Enter PAN number" className="donate-now-input" readOnly={isExisting && !!fetchedData.panNo} />
                </div>
              </div>

              <div className="donate-now-field">
                <label className="donate-now-label">State <span style={{ color: 'red' }}>*</span></label>
                <SearchableSelect name="state" value={formData.state} options={allStates} onChange={handleStateChange} placeholder="Select or type State" icon={FaMap} required readOnly={isExisting && !!fetchedData.state} disabled={false} />
              </div>

              <div className="donate-now-field">
                <label className="donate-now-label">District <span style={{ color: 'red' }}>*</span></label>
                <SearchableSelect name="district" value={formData.district} options={availableDistricts} onChange={handleChange} placeholder={formData.state ? "Select or type District" : "Please select State first"} icon={FaCity} required readOnly={isExisting && !!fetchedData.district} disabled={!formData.state} />
              </div>

              <div className="donate-now-field donate-now-full-width">
                <label className="donate-now-label">Current Address <span style={{ color: 'red' }}>*</span></label>
                <div className="donate-now-input-wrap donate-now-textarea-wrap">
                  <FaMapMarkerAlt className="donate-now-input-icon donate-now-textarea-icon" />
                  <textarea name="currentAddress" value={formData.currentAddress} onChange={handleCurrentAddressChange} placeholder="Enter current address" className="donate-now-textarea" rows="3" required readOnly={isExisting && !!fetchedData.currentAddress}></textarea>
                </div>
              </div>

              <div className="donate-now-field donate-now-full-width donate-now-checkbox-field">
                <label className="donate-now-checkbox-label">
                  <input type="checkbox" name="isSameAddress" checked={formData.isSameAddress} onChange={handleSameAddressCheckboxChange} disabled={isExisting && !!fetchedData.permanentAddress} />
                  <span>Permanent address is same as Current address</span>
                </label>
              </div>

              <div className="donate-now-field donate-now-full-width">
                <label className="donate-now-label">Permanent Address <span style={{ color: 'red' }}>*</span></label>
                <div className="donate-now-input-wrap donate-now-textarea-wrap">
                  <FaMapMarkerAlt className="donate-now-input-icon donate-now-textarea-icon" />
                  <textarea name="permanentAddress" value={formData.permanentAddress} onChange={(e) => setFormData(prev => ({ ...prev, permanentAddress: e.target.value }))} placeholder={formData.isSameAddress ? "Same as Current Address" : "Enter permanent address"} className="donate-now-textarea" rows="3" required readOnly={formData.isSameAddress || (isExisting && !!fetchedData.permanentAddress)}></textarea>
                </div>
              </div>

            </div>
            <div className="donate-now-button-wrap">
              <button type="submit" className="donate-now-submit-btn"><FaDonate /> Submit Donation</button>
            </div>
          </form>
        </div>
      </div>
    </div>
    </>
  );
};

export default DonateNow;